import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { finalize } from 'rxjs/operators';

import {
  SpsListenTyp,
  SpsLoeschErgebnis,
  SpsLoeschQuelle,
  SpsPruefstatus,
  SpsSignalLoeschModus,
  SpsSignalLoeschvorschau,
  listenTypInfo,
  projektListenTypVonSps,
  pruefstatusText
} from '../../../../../Models/Sps';
import { SpsPruefungApiService } from '../../../../../Services/sps-pruefung-api.service';

export interface SpsSignalLoeschenDialogData {
  signalId: number;
  name: string | null;
  adresse: string | null;
  pruefstatus: SpsPruefstatus;
}

/**
 * Löschdialog der SPS-Prüfung mit zwei Wegen:
 *
 *  „Nur aus Prüfung entfernen“  Das Signal verschwindet aus der Prüfung. Die Herkunftszeilen
 *                               (SPS-Zuordnungsliste, PLC-Tag-Liste) bleiben in ihren Listen
 *                               und werden nicht erneut verknüpft – auch nicht durch den
 *                               automatischen Abgleich oder ein Listenupdate.
 *  „Vollständig löschen“        Signal, Prüfverlauf und ALLE Herkunftszeilen werden gelöscht.
 *                               Andere Zeilen und Signale sind nie betroffen.
 *
 * Lädt die Löschvorschau (welche Zeilen betroffen sind) und führt die gewählte
 * Löschung selbst aus. Liefert das Backend-Ergebnis oder null (abgebrochen).
 */
@Component({
  selector: 'app-sps-signal-loeschen-dialog',
  template: `
    <h2 mat-dialog-title>Signal löschen</h2>

    <mat-dialog-content>
      <p class="signal">
        <strong>{{ data.name || '–' }}</strong>
        <span class="adresse" *ngIf="data.adresse">{{ data.adresse }}</span>
        <span class="status" *ngIf="data.pruefstatus !== 'Ungeprueft'">Prüfstatus: {{ statusText }}</span>
      </p>

      <mat-progress-bar mode="indeterminate" *ngIf="laedt"></mat-progress-bar>

      <div class="fehler" *ngIf="fehler">
        <mat-icon inline>error_outline</mat-icon> {{ fehler }}
      </div>

      <ng-container *ngIf="vorschau as v">
        <h3 class="abschnitt">Betroffene Herkunftseinträge ({{ v.quellen.length }})</h3>

        <p class="leer" *ngIf="v.quellen.length === 0">
          Dieses Signal hat keine Herkunftszeile mehr (archiviert).
        </p>

        <ul class="quellen">
          <li *ngFor="let q of v.quellen">
            <mat-icon inline>{{ icon(q.listeTyp) }}</mat-icon>
            <span class="quelle-liste">{{ typTitel(q.listeTyp) }} „{{ q.listeName }}“, Zeile {{ q.excelZeile }}</span>
            <span class="quelle-detail">{{ q.signalname || '–' }}<ng-container *ngIf="q.adresseOriginal"> · {{ q.adresseOriginal }}</ng-container></span>
          </li>
        </ul>

        <div class="option option-sanft">
          <strong>Nur aus Prüfung entfernen</strong>
          <p>
            Das Signal verschwindet aus der SPS-Prüfung<ng-container *ngIf="v.geprueft">; Prüfstatus und Verlauf bleiben archiviert</ng-container>.
            {{ v.quellen.length === 1 ? 'Die Herkunftszeile bleibt' : 'Die ' + v.quellen.length + ' Herkunftszeilen bleiben' }}
            in der jeweiligen Liste erhalten und werden nicht erneut in die Prüfung aufgenommen – auch nicht durch den
            automatischen Abgleich oder ein Listenupdate.
          </p>
        </div>

        <div class="option option-hart">
          <strong>Vollständig löschen</strong>
          <p>
            Das Signal wird samt Prüfverlauf<ng-container *ngIf="v.protokollAnzahl > 0"> ({{ v.protokollAnzahl }} Einträge)</ng-container>
            gelöscht, zusammen mit
            <ng-container *ngIf="v.quellenZuordnungsliste > 0">{{ v.quellenZuordnungsliste }} Zeile{{ v.quellenZuordnungsliste === 1 ? '' : 'n' }} der SPS-Zuordnungsliste</ng-container><ng-container *ngIf="v.quellenZuordnungsliste > 0 && v.quellenPlcTags > 0"> und </ng-container><ng-container *ngIf="v.quellenPlcTags > 0">{{ v.quellenPlcTags }} Zeile{{ v.quellenPlcTags === 1 ? '' : 'n' }} der PLC-Tag-Liste</ng-container><ng-container *ngIf="v.quellen.length === 0">keinen Herkunftszeilen</ng-container>.
            Andere Zeilen und Signale sind nicht betroffen. Dieser Schritt kann nicht rückgängig gemacht werden.
          </p>
        </div>
      </ng-container>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button type="button" (click)="abbrechen()" [disabled]="loescht">Abbrechen</button>
      <button mat-stroked-button color="primary" type="button" (click)="loeschen('NurAusPruefung')" [disabled]="!bereit">
        <mat-spinner diameter="16" *ngIf="loescht && modus === 'NurAusPruefung'"></mat-spinner>
        Nur aus Prüfung entfernen
      </button>
      <button mat-raised-button color="warn" type="button" (click)="loeschen('Vollstaendig')" [disabled]="!bereit">
        <mat-spinner diameter="16" *ngIf="loescht && modus === 'Vollstaendig'"></mat-spinner>
        Vollständig löschen
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    mat-dialog-content { width: 560px; max-width: 100%; }
    .signal { display: flex; flex-wrap: wrap; gap: 8px 12px; align-items: baseline; margin: 0 0 12px; }
    .adresse { font-family: ui-monospace, Consolas, monospace; color: #666; }
    .status { font-size: 13px; color: #666; }
    .abschnitt { margin: 12px 0 6px; font-size: 14px; font-weight: 600; }
    .leer { margin: 0 0 12px; color: #666; font-size: 13px; }
    .quellen { list-style: none; margin: 0 0 12px; padding: 0; display: flex; flex-direction: column; gap: 6px; }
    .quellen li { display: grid; grid-template-columns: auto 1fr; column-gap: 8px; align-items: baseline; font-size: 13px; }
    .quellen mat-icon { grid-row: span 2; color: #666; }
    .quelle-liste { font-weight: 500; }
    .quelle-detail { color: #666; }
    .option { padding: 10px 12px; border-radius: 6px; margin-bottom: 8px; }
    .option p { margin: 4px 0 0; font-size: 13px; }
    .option-sanft { background: #e3f2fd; border-left: 4px solid #1976d2; }
    .option-hart { background: #ffebee; border-left: 4px solid #c62828; }
    .fehler { display: flex; gap: 6px; align-items: center; color: #b71c1c; margin: 8px 0; }
    mat-dialog-actions { flex-wrap: wrap; gap: 8px; }
    mat-dialog-actions mat-spinner { display: inline-block; margin-right: 6px; }
  `]
})
export class SpsSignalLoeschenDialogComponent implements OnInit {

  vorschau: SpsSignalLoeschvorschau | null = null;
  laedt = false;
  loescht = false;
  fehler = '';
  modus: SpsSignalLoeschModus | null = null;

  constructor(
    private dialogRef: MatDialogRef<SpsSignalLoeschenDialogComponent, SpsLoeschErgebnis | null>,
    @Inject(MAT_DIALOG_DATA) public data: SpsSignalLoeschenDialogData,
    private api: SpsPruefungApiService
  ) { }

  ngOnInit(): void {
    this.laedt = true;

    this.api.getLoeschvorschau(this.data.signalId)
      .pipe(finalize(() => this.laedt = false))
      .subscribe({
        next: v => this.vorschau = v,
        error: err => this.fehler = err?.status === 404
          ? 'Das Signal existiert nicht mehr.'
          : SpsPruefungApiService.fehlertext(err, 'Die Auswirkungen konnten nicht ermittelt werden.')
      });
  }

  get statusText(): string {
    return pruefstatusText(this.data.pruefstatus);
  }

  get bereit(): boolean {
    return !!this.vorschau && !this.laedt && !this.loescht;
  }

  typTitel(typ: SpsListenTyp): string {
    return listenTypInfo(projektListenTypVonSps(typ)).titel;
  }

  icon(typ: SpsListenTyp): string {
    return listenTypInfo(projektListenTypVonSps(typ)).icon;
  }

  trackByQuelle(_index: number, q: SpsLoeschQuelle): number {
    return q.eintragId;
  }

  loeschen(modus: SpsSignalLoeschModus): void {
    if (!this.bereit) {
      return;
    }

    this.modus = modus;
    this.loescht = true;
    this.fehler = '';

    this.api.deleteSignal(this.data.signalId, modus)
      .pipe(finalize(() => this.loescht = false))
      .subscribe({
        next: ergebnis => this.dialogRef.close(ergebnis),
        error: err => this.fehler = SpsPruefungApiService.fehlertext(err, 'Das Signal konnte nicht gelöscht werden.')
      });
  }

  abbrechen(): void {
    this.dialogRef.close(null);
  }
}
